/* Copyright (c) 2021 MOCA Systems, Inc.  All rights reserved. */

import {
	Component,
	OnInit,
	AfterViewInit,
	OnChanges,
	OnDestroy,
	Input,
	Output,
	EventEmitter,
	ViewChild,
	ElementRef,
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	SimpleChanges,
} from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import * as Hammer from 'hammerjs';
import { ResizableContainerComponent } from '../resizable-container/resizable-container.component';
import { ContainerLayerManager } from '../base-container/ContainerLayerManager';

@Component({
	selector: 'draggable-container',
	templateUrl: './draggable-container.component.html',
	styleUrls: [
		'../resizable-container/resizable-container.component.scss',
		'./draggable-container.component.scss',
	],
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DraggableContainerComponent
	extends ResizableContainerComponent
	implements OnInit, AfterViewInit, OnChanges, OnDestroy
{
	@Input() public draggable = true;
	@Input() public resizable = true;
	@Input() public showIcon = true;
	@Input() public iconType = 'move'; // supports move or close for now

	@Output() public close: EventEmitter<any> = new EventEmitter();

	@ViewChild('container', { static: false }) public container: ElementRef;
	@ViewChild('dragArea', { static: false }) public dragArea: ElementRef;
	@ViewChild('resizeHandle', { static: false }) public resizeHandle: ElementRef;

	public draggable$ = new BehaviorSubject(true);
	public resizable$ = new BehaviorSubject(true);
	public isDragging: boolean;
	public hasBeenDragged: boolean;

	private dragStartHandler: (MouseEvent) => void;
	private dragHandler: (MouseEvent) => void;
	private dragEndHandler: () => void;

	private touchStartHandler: (MouseEvent) => void;
	private touchMoveHandler: (MouseEvent) => void;
	private touchEndHandler: (MouseEvent) => void;

	constructor(
		protected changeDetectorRef: ChangeDetectorRef,
		protected containerLayerManager: ContainerLayerManager
	) {
		super(changeDetectorRef, containerLayerManager);

		this.dragStartHandler = (event) => this.dragStart(event);
		this.dragHandler = (event) => this.drag(event);
		this.dragEndHandler = () => this.dragEnd();
		this.touchStartHandler = (e) => this.touchStart(e);
		this.touchMoveHandler = (e) => this.touchMove(e);
		this.touchEndHandler = () => this.touchEnd();
	}

	public ngOnInit() {}

	public ngAfterViewInit() {
		super.ngAfterViewInit();

		// if min's unspecified, use current auto calculated dimensions as min's
		this.minWidth = this.minWidth || this.container.nativeElement.offsetWidth;
		this.minHeight = this.minHeight || this.container.nativeElement.offsetHeight;

		if (this.draggable) {
			this.dragEvents();
		}

		this.draggable$.next(this.draggable);
	}

	public ngOnChanges(changes: SimpleChanges) {
		const draggableChange = changes['draggable'];

		if (
			draggableChange &&
			draggableChange.previousValue !== undefined &&
			draggableChange.previousValue !== draggableChange.currentValue
		) {
			if (draggableChange.currentValue) {
				this.draggable$.next(draggableChange.currentValue);
				this.changeDetectorRef.detectChanges();
				this.dragEvents(draggableChange.currentValue);
			} else {
				this.dragEvents(draggableChange.currentValue);
				this.draggable$.next(draggableChange.currentValue);
				this.changeDetectorRef.detectChanges();
			}
		}
	}

	public ngOnDestroy() {
		// this.changeDetectorRef.detach();
		this.destroy();
	}

	protected destroy(): void {
		super.destroy();

		if (this.draggable) {
			this.dragEvents(false);
		}

		this.draggable$.complete();
		this.draggable$ = null;
	}

	private dragEvents(enable: boolean = true): void {
		const dragArea: HTMLElement = this.dragArea.nativeElement;
		const event = enable ? 'on' : 'off';
		Hammer[event](dragArea, 'mousedown', this.dragStartHandler);
		Hammer[event](window, 'mousemove', this.dragHandler);
		Hammer[event](window, 'mouseup', this.dragEndHandler);
		Hammer[event](dragArea, 'touchstart', this.touchStartHandler);
		Hammer[event](window, 'touchmove', this.touchMoveHandler);
		Hammer[event](window, 'touchend', this.touchEndHandler);
		this.dragEnd();
	}

	private dragStart(event): void {
		this.isDragging = true;
		this.originalX = this.container.nativeElement.offsetLeft;
		this.originalY = this.container.nativeElement.offsetTop;
		this.offsetX = event.clientX;
		this.offsetY = event.clientY;
		this.hasBeenDragged = true;
	}

	private drag(event): void {
		if (!this.isDragging) {
			return;
		}

		const x = this.originalX + event.clientX - this.offsetX;
		const y = this.originalY + event.clientY - this.offsetY;
		super.updatePosition(x, y);
	}

	private dragEnd(): void {
		this.isDragging = false;
	}

	private touchStart(e): void {
		this.dragStart(e.targetTouches[0]);
	}

	private touchMove(e): void {
		const t = e.targetTouches;
		if (!t || t.length > 1) {
			return;
		}
		this.drag(t[0]);
	}

	private touchEnd(): void {
		this.dragEnd();
	}
}
